import React from "react";
import "./HTML_MAIN_CSS.css";

const HTML_Geolocation = () => {
  return (
    <div className="html-container">
      <h1>HTML Geolocation API – Detecting User Location</h1>

      {/* 1. Introduction */}
      <section className="html-section">
        <h2>1. Introduction</h2>
        <p>
          The <strong>HTML Geolocation API</strong> allows web applications to access a user’s
          geographical location with their permission. This enables dynamic and personalized
          user experiences such as finding nearby stores, tracking deliveries, or mapping
          routes.
        </p>
        <p>
          It works through the <code>navigator.geolocation</code> object in JavaScript, which
          interacts with the browser to obtain GPS, Wi-Fi, or IP-based location data.
        </p>
      </section>

      {/* 2. How Geolocation Works */}
      <section className="html-section">
        <h2>2. How Geolocation Works</h2>
        <ul>
          <li>The browser asks the user for permission to access their location.</li>
          <li>Once granted, the browser retrieves coordinates from GPS, Wi-Fi, or IP address.</li>
          <li>The API returns a <strong>position object</strong> containing latitude and longitude.</li>
        </ul>
        <p>
          Note: Geolocation accuracy depends on the user’s device and network connectivity.
        </p>
      </section>

      {/* 3. Basic Syntax */}
      <section className="html-section">
        <h2>3. Basic Syntax</h2>
        <pre className="code-block">
          <code>{`navigator.geolocation.getCurrentPosition(successCallback, errorCallback);`}</code>
        </pre>
        <ul>
          <li><strong>successCallback:</strong> Function called if location retrieval succeeds.</li>
          <li><strong>errorCallback:</strong> Function called if retrieval fails.</li>
        </ul>
      </section>

      {/* 4. Example: Get User Location */}
      <section className="html-section">
        <h2>4. Example – Get User Location</h2>
        <p>
          This example shows how to get the user’s latitude and longitude and display it on the page.
        </p>

        <pre className="code-block">
          <code>{`<button onclick="getLocation()">Get My Location</button>
<p id="output"></p>

<script>
function getLocation() {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(showPosition, showError);
  } else {
    document.getElementById("output").innerHTML = 
      "Geolocation is not supported by this browser.";
  }
}

function showPosition(position) {
  document.getElementById("output").innerHTML = 
    "Latitude: " + position.coords.latitude + 
    "<br>Longitude: " + position.coords.longitude;
}

function showError(error) {
  switch(error.code) {
    case error.PERMISSION_DENIED:
      document.getElementById("output").innerHTML = "User denied permission.";
      break;
    case error.POSITION_UNAVAILABLE:
      document.getElementById("output").innerHTML = "Location unavailable.";
      break;
    case error.TIMEOUT:
      document.getElementById("output").innerHTML = "Request timed out.";
      break;
    default:
      document.getElementById("output").innerHTML = "Unknown error occurred.";
  }
}
</script>`}</code>
        </pre>
      </section>

      {/* 5. Watch Position */}
      <section className="html-section">
        <h2>5. Watching the User’s Location</h2>
        <p>
          The <code>watchPosition()</code> method continually monitors location changes, useful
          for real-time tracking apps such as delivery or fitness trackers.
        </p>

        <pre className="code-block">
          <code>{`let watchID = navigator.geolocation.watchPosition(showPosition);

function showPosition(position) {
  console.log("Latitude: " + position.coords.latitude);
  console.log("Longitude: " + position.coords.longitude);
}

// To stop watching
navigator.geolocation.clearWatch(watchID);`}</code>
        </pre>
      </section>

      {/* 6. Position Object */}
      <section className="html-section">
        <h2>6. The Position Object</h2>
        <p>
          The success callback function receives a <code>position</code> object containing:
        </p>

        <table className="styled-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>coords.latitude</code></td>
              <td>The latitude in decimal degrees.</td>
            </tr>
            <tr>
              <td><code>coords.longitude</code></td>
              <td>The longitude in decimal degrees.</td>
            </tr>
            <tr>
              <td><code>coords.accuracy</code></td>
              <td>Accuracy level of the coordinates (in meters).</td>
            </tr>
            <tr>
              <td><code>timestamp</code></td>
              <td>The date and time when the position was retrieved.</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 7. Handling Errors */}
      <section className="html-section">
        <h2>7. Handling Geolocation Errors</h2>
        <p>
          The <code>errorCallback</code> function helps you manage possible failures in location retrieval.
        </p>
        <pre className="code-block">
          <code>{`function showError(error) {
  switch(error.code) {
    case error.PERMISSION_DENIED:
      alert("Permission denied by the user.");
      break;
    case error.POSITION_UNAVAILABLE:
      alert("Position information unavailable.");
      break;
    case error.TIMEOUT:
      alert("Request timed out.");
      break;
    default:
      alert("An unknown error occurred.");
  }
}`}</code>
        </pre>
      </section>

      {/* 8. Use Cases */}
      <section className="html-section">
        <h2>8. Common Use Cases</h2>
        <ul>
          <li>Showing user location on a map.</li>
          <li>Providing location-based recommendations.</li>
          <li>Tracking delivery or fleet movement.</li>
          <li>Personalizing content based on location.</li>
        </ul>
      </section>

      {/* 9. Privacy and Security */}
      <section className="html-section">
        <h2>9. Privacy and Security</h2>
        <ul>
          <li>Users must grant explicit permission before location access.</li>
          <li>Geolocation only works over <strong>HTTPS</strong> for security.</li>
          <li>Never store or share user coordinates without consent.</li>
        </ul>
      </section>

      {/* 10. Summary */}
      <section className="html-section">
        <h2>10. Summary</h2>
        <ul>
          <li>The Geolocation API enables real-world location access in browsers.</li>
          <li>Use <code>getCurrentPosition()</code> for one-time access and <code>watchPosition()</code> for tracking.</li>
          <li>Always handle permissions and errors gracefully.</li>
          <li>Implement HTTPS and protect user privacy.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTML_Geolocation;
